import { fanficsCatalogSortMapping } from '@/lib/fanfics-catalog-api';
import { isValidFanficsCatalogSort } from '../resolve-permalink-sort';

export interface PermalinkQueryParams {
  page?: string | string[];
  sort?: string | string[];
  ratings?: string | string[];
  tags?: string | string[];
  statuses?: string | string[];
  universes?: string | string[];
  characters?: string | string[];
}

const toStringParam = (value: string | string[] | undefined): string => {
  if (!value) return '';
  return Array.isArray(value) ? value[0] || '' : value;
};

interface BuildFanficsQueryParamsOptions {
  type: 'fanfic' | 'original';
  fandomSlug?: string;
  characterSlug?: string;
  pairingSlug?: string;
  tagSlug?: string;
  searchParams: PermalinkQueryParams;
}

/**
 * Builds URLSearchParams for /api/fanfics-custom/v2 directly from permalink slug
 * segments plus the "pass-through" GET filters (ratings/tags/statuses/characters/…)
 * that remain regular query params on top of a permanent SEO URL.
 *
 * Example: `/fanfics/fandoms/genshin-impact?characters=66171` must send both
 * `fandomSlug` and `characters` so SSR matches the soft-nav client request.
 */
export const buildFanficsQueryParamsForPermalink = ({
  type,
  fandomSlug,
  characterSlug,
  pairingSlug,
  tagSlug,
  searchParams,
}: BuildFanficsQueryParamsOptions): URLSearchParams => {
  const queryParams = new URLSearchParams();

  const page = parseInt(toStringParam(searchParams.page), 10) || 1;
  const sortParam = toStringParam(searchParams.sort);
  const sortBy = isValidFanficsCatalogSort(sortParam) ? sortParam : 'hotNew';

  queryParams.set('page', page.toString());
  queryParams.set('limit', '20');
  queryParams.set('type', type);
  queryParams.set('sort', fanficsCatalogSortMapping[sortBy]);
  queryParams.set('includePromo', '1');

  if (fandomSlug) queryParams.set('fandomSlug', fandomSlug);
  if (characterSlug) queryParams.set('characterSlug', characterSlug);
  if (pairingSlug) queryParams.set('pairingSlug', pairingSlug);
  if (tagSlug) queryParams.set('tagSlug', tagSlug);

  const ratings = toStringParam(searchParams.ratings);
  if (ratings) queryParams.set('rating', ratings);

  const tags = toStringParam(searchParams.tags);
  if (tags) queryParams.set('tags', tags);

  const statuses = toStringParam(searchParams.statuses);
  if (statuses) queryParams.set('postStatus', statuses);

  const universes = toStringParam(searchParams.universes);
  if (universes) queryParams.set('universe', universes);

  // Pass-through character ids on a fandom (or other) permalink when the
  // character itself has no SEO slug. Skip when characterSlug already encodes
  // identity — same priority as fanfics-catalog-api client builder.
  const characters = toStringParam(searchParams.characters);
  if (characters && !characterSlug) {
    queryParams.set('characters', characters);
  }

  return queryParams;
};
